/**
 * The consent window's three buttons in the site's own words and language.
 *
 * InMobi Choice takes the page's `<html lang>` and has its own button texts:
 * in capitals ("ACCEPT ALL"), in English for about half of the site's 87
 * locales, "SAVE & EXIT" in English everywhere, and some wrong ("ПРИНЯТЬ
 * ВСЕХ", Simplified Chinese for zh-Hant) — measured on prod 2026-11-03.
 * Custom texts are a paid platform feature, so the labels come from the
 * site's catalogs (`common:consent_window.*`) in the design's sentence case
 * ("Accept all", "Reject all", "Save my choices").
 *
 * The platform renders and re-renders its UI on its own schedule (first
 * show, re-open from "Cookie settings", layer changes), so a DOM observer
 * re-applies the labels whenever they drift. It only writes a differing
 * text, so its own writes settle instead of looping. Never throws: a
 * failure leaves the platform's labels.
 */
export interface ConsentWindowLabels {
  readonly acceptAll: string
  readonly rejectAll: string
  readonly saveMyChoices: string
}

const BUTTONS: readonly (readonly [string, keyof ConsentWindowLabels])[] = [
  ['#accept-all-btn', 'acceptAll'],
  ['#reject-all-btn', 'rejectAll'],
  ['#save-and-exit', 'saveMyChoices'],
]

/** Apply once; true when anything changed. Exported for tests. */
export function applyConsentWindowLabels(root: ParentNode, labels: ConsentWindowLabels): boolean {
  let changed = false
  for (const [selector, key] of BUTTONS) {
    const label = labels[key]
    if (!label) continue
    for (const button of root.querySelectorAll<HTMLElement>(selector)) {
      if (button.textContent !== label) {
        button.textContent = label
        changed = true
      }
    }
  }
  return changed
}

/** The platform's root, appended to `<body>` (`#qc-cmp2-container`). */
const CONTAINER_ID = 'qc-cmp2-container'

/**
 * Keep the buttons labelled for the life of the page. `labels` is read on
 * every pass, so a catalog that arrives after the window still wins.
 *
 * Only the platform's own subtree is watched — the map rewrites the rest of
 * the DOM all the time — and `<body>` only for its direct children, until
 * the container appears.
 */
export function localizeConsentWindow(labels: () => ConsentWindowLabels): void {
  try {
    const watchContainer = (container: HTMLElement) => {
      const apply = () => {
        try {
          applyConsentWindowLabels(container, labels())
        } catch {
          // Leave the platform's labels (safe-client #2).
        }
      }
      new MutationObserver(apply).observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      })
      apply()
    }
    const existing = document.getElementById(CONTAINER_ID)
    if (existing) {
      watchContainer(existing)
      return
    }
    const waitForContainer = new MutationObserver(() => {
      const container = document.getElementById(CONTAINER_ID)
      if (!container) return
      waitForContainer.disconnect()
      watchContainer(container)
    })
    waitForContainer.observe(document.body, { childList: true })
  } catch {
    // No MutationObserver: the platform's labels stay.
  }
}
